<template>
  <div class="chat-area" ref="chatArea">
    <div v-if="messages.length === 0" class="welcome-container">
      <div class="welcome-logo">
        <img src="/static/img/ai-logo.png" alt="AI Logo" onerror="this.src='data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0iIzNjODJmNiI+PHBhdGggZD0iTTEyIDJhMTAgMTAgMCAxIDAgMTAgMTBBMTAgMTAgMCAwIDAgMTIgMnptLTIgMTQuNVY3LjVsNiA0LjVsLTYgNC41eiIvPjwvc3ZnPg=='" />
      </div>
      <h2 class="welcome-title">欢迎使用AI助手</h2>
      <p class="welcome-subtitle">我可以回答问题、讲解知识、辅助学习</p>

      <div class="suggested-questions">
        <h3 class="questions-title">你可以尝试这些问题：</h3>
        <div class="question-buttons">
          <button @click="suggestQuestion('你能做什么？')" class="question-button">
            <span class="question-icon">💡</span>
            <span class="question-text">你能做什么？</span>
          </button>
          <button @click="suggestQuestion('请给我讲解一下三角形的面积公式')" class="question-button">
            <span class="question-icon">📐</span>
            <span class="question-text">请讲解三角形的面积公式</span>
          </button>
          <button @click="suggestQuestion('帮我写一篇关于环保的作文')" class="question-button">
            <span class="question-icon">📝</span>
            <span class="question-text">帮我写一篇环保作文</span>
          </button>
          <button @click="suggestQuestion('解释一下光合作用的过程')" class="question-button">
            <span class="question-icon">🌱</span>
            <span class="question-text">解释光合作用的过程</span>
          </button>
        </div>
      </div>
    </div>

    <template v-else>
      <div class="messages-container">
        <div v-for="(message, index) in messages" :key="index" class="message-wrapper">
          <div class="message-date" v-if="shouldShowDate(index)">
            {{ formatDate(getCurrentDate()) }}
          </div>

          <UserMessage
            v-if="message.role === 'user'"
            :content="message.content"
          />

          <AiMessage
            v-else
            :content="message.content"
            :is-error="message.isError"
            :is-system="message.isSystem"
          />
        </div>
      </div>

      <div v-if="loading" class="message-loading">
        <div class="loading-container">
          <div class="ai-avatar loading-avatar">AI</div>
          <div class="loading-bubble">
            <div class="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import UserMessage from './UserMessage.vue'
import AiMessage from './AiMessage.vue'

export default {
  components: {
    UserMessage,
    AiMessage
  },
  props: {
    messages: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chatArea: null,
      lastDateShown: null
    }
  },
  methods: {
    scrollToBottom() {
      if (this.$refs.chatArea) {
        this.$refs.chatArea.scrollTop = this.$refs.chatArea.scrollHeight
      }
    },
    suggestQuestion(question) {
      this.$emit('suggest-question', question)
    },
    shouldShowDate(index) {
      // 在第一条消息或每天的第一条消息前显示日期
      if (index === 0) {
        this.lastDateShown = this.getCurrentDate()
        return true
      }
      return false
    },
    getCurrentDate() {
      return new Date()
    },
    formatDate(date) {
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      if (date.toDateString() === today.toDateString()) {
        return '今天'
      } else if (date.toDateString() === yesterday.toDateString()) {
        return '昨天'
      } else {
        return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`
      }
    }
  },
  watch: {
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      },
      deep: true
    },
    loading() {
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    }
  }
}
</script>

<style scoped>
.chat-area {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
  position: relative;
  padding: 0 5px;
  margin-bottom: 8px;
  background-color: #f9fafb;
  border-radius: 12px;
}

.chat-area::-webkit-scrollbar {
  width: 6px;
}

.chat-area::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 10px;
}

.chat-area::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 10px;
}

.welcome-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  height: 100%;
  text-align: center;
}

.welcome-logo {
  width: 80px;
  height: 80px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(59, 130, 246, 0.1);
  padding: 16px;
}

.welcome-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.welcome-title {
  font-size: 28px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
}

.welcome-subtitle {
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 36px;
}

.suggested-questions {
  width: 100%;
  max-width: 600px;
}

.questions-title {
  font-size: 16px;
  font-weight: 500;
  color: #4b5563;
  margin-bottom: 16px;
  text-align: left;
}

.question-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.question-button {
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.question-button:hover {
  border-color: #3b82f6;
  box-shadow: 0 1px 3px rgba(59, 130, 246, 0.3);
}

.question-icon {
  font-size: 20px;
  margin-right: 12px;
}

.question-text {
  font-size: 14px;
  color: #1f2937;
  font-weight: 500;
}

.messages-container {
  padding: 16px 8px;
}

.message-wrapper {
  margin-bottom: 24px;
}

.message-date {
  text-align: center;
  margin: 24px 0 16px;
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.message-loading {
  padding: 0 16px 16px;
}

.loading-container {
  display: flex;
  align-items: flex-start;
}

.loading-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  flex-shrink: 0;
  background-color: #3b82f6;
  color: white;
  margin-right: 12px;
}

.loading-bubble {
  background-color: white;
  border-radius: 12px;
  border-top-left-radius: 4px;
  padding: 12px 16px;
  min-width: 60px;
  min-height: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.typing-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
}

.typing-indicator span {
  height: 8px;
  width: 8px;
  margin: 0 2px;
  background-color: #3b82f6;
  border-radius: 50%;
  display: inline-block;
  opacity: 0.4;
}

.typing-indicator span:nth-child(1) {
  animation: bounce 1.2s infinite;
}

.typing-indicator span:nth-child(2) {
  animation: bounce 1.2s infinite 0.3s;
}

.typing-indicator span:nth-child(3) {
  animation: bounce 1.2s infinite 0.6s;
}

@keyframes bounce {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-5px);
  }
}
</style>
